import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../core/client';
import { roomPlanKeys } from '../../core/query-keys';
import type { RoomMode, RoomPlan, RoomSettings, SetLightsOnBody, SetModeBody } from './room-plans.types';

const BASE = '/v1/production/plans';

/** Every room that currently has a plan issued. */
export function useRoomPlans() {
  return useQuery({
    queryKey: roomPlanKeys.list,
    queryFn: () => api.get<RoomPlan[]>(BASE),
    refetchInterval: 10_000,
  });
}

/**
 * One room's plan.
 *
 * The backend answers 404 ("no plan is issued for this room") deliberately —
 * `plan_view.rs`: "never an empty 200, because 'no plan' and 'plan with
 * nothing in it' mean very different things." A room with no plan is a STATE
 * an operator screen must render (never planted, or taken off automatic), not
 * a fetch failure — so a 404 resolves to `null` data here and every other
 * status stays a real, surfaced error.
 */
export function useRoomPlan(room: string | null | undefined) {
  return useQuery({
    queryKey: roomPlanKeys.one(room ?? ''),
    queryFn: async (): Promise<RoomPlan | null> => {
      try {
        return await api.get<RoomPlan>(`${BASE}/${room}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!room,
    refetchInterval: 10_000,
  });
}

/** A room's automation settings — answers whether or not a plan is issued
 * (mode, who/why/when it changed, the lights-on anchor). Always answers, even
 * for a room with no plan at all (F6). */
export function useRoomSettings(room: string | null | undefined) {
  return useQuery({
    queryKey: roomPlanKeys.settings(room ?? ''),
    queryFn: () => api.get<RoomSettings>(`${BASE}/${room}/settings`),
    enabled: !!room,
    refetchInterval: 10_000,
  });
}

/** Take a room off automatic, or hand it back. `reason` is required by the
 * server for `mode: 'manual'`. Invalidates the room's plan + settings — a
 * manual write withdraws the plan in the SAME request (`pending_apply: false`),
 * so the plan cache is stale the instant this resolves. */
export function useSetRoomMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ room, body }: { room: string; body: SetModeBody }) =>
      api.post<RoomMode>(`${BASE}/${room}/mode`, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: roomPlanKeys.one(vars.room) });
      qc.invalidateQueries({ queryKey: roomPlanKeys.settings(vars.room) });
    },
  });
}

/** Record (or clear) a room's lights-on minute — the anchor the whole
 * photoperiod hangs off. `null` clears it, and a room with no lights-on time
 * gets no plan at all. */
export function useSetLightsOn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ room, body }: { room: string; body: SetLightsOnBody }) =>
      api.put<RoomMode>(`${BASE}/${room}/lights-on`, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: roomPlanKeys.one(vars.room) });
      qc.invalidateQueries({ queryKey: roomPlanKeys.settings(vars.room) });
    },
  });
}
